import { positionMilli } from "./board.js";
import { MIN_DAMAGE_PERMILLE } from "./constants.js";
import { towerStats, unitStats } from "./data.js";
import { distSq, mulPermille } from "./fixed.js";
import { isTargetable, type FireOrder, type TowerState } from "./towers.js";
import type { BattleEvent, Coord, Tick, TowerStats, UnitKind, UnitStatus } from "./types.js";

/**
 * Step 7 of the tick: the shots taken in step 6 land.
 *
 * Kept apart from targeting so each can be tested on its own — and because the
 * tick order in 02 GDD §7 genuinely separates them: every tower in a tick picks
 * its target against the same board state, then all the damage applies. A tower
 * cannot benefit from a unit another tower killed in the same tick.
 */

/** The part of a unit damage needs. UnitState satisfies this. */
export interface Combatant {
  readonly id: number;
  readonly kind: UnitKind;
  hp: number;
  progress: number;
  status: UnitStatus;
  slowUntilTick: Tick;
  slowPermille: number;
}

export interface DamageContext {
  readonly tick: Tick;
  readonly path: readonly Coord[];
  readonly units: readonly Combatant[];
  readonly towers: readonly TowerState[];
  readonly events: BattleEvent[];
}

/**
 * Damage after armour (02 §5, Assumptions "Min damage fraction after armor").
 *
 *     dealt = max(raw x 20%, raw - armor)
 *
 * The floor stops heavy armour from making a unit completely immune, which
 * would be a dead end for the attacker rather than a puzzle.
 *
 * The Ballista ignores armour entirely — that is its whole job, and it is the
 * only tower that does.
 */
export function damageAfterArmor(raw: number, armor: number, piercesArmor: boolean): number {
  if (piercesArmor) return raw;
  const floor = mulPermille(raw, MIN_DAMAGE_PERMILLE);
  return Math.max(floor, raw - armor);
}

/** Apply every shot fired this tick. */
export function applyFireOrders(context: DamageContext, orders: readonly FireOrder[]): void {
  for (const order of orders) {
    const tower = context.towers[order.towerId];
    const target = context.units[order.unitId];
    if (!tower || !target) continue;

    const stats = towerStats(tower.kind, tower.level);

    strike(context, tower, stats, target, order.damage);

    if (stats.splashRadiusMilli > 0) {
      splash(context, tower, stats, target, order.damage);
    }
  }
}

/**
 * Everything within the splash radius of the unit that was hit takes the same
 * damage (02 GDD §6.3, "Splash hits all in radius"). This is what makes the
 * Brazier answer a swarm and the Cannon answer a stack of rams.
 *
 * Splash is measured from where the TARGET is, not from the tower, so a shot
 * that lands mid-corner catches whatever is around the corner too.
 */
function splash(
  context: DamageContext,
  tower: TowerState,
  stats: TowerStats,
  target: Combatant,
  raw: number
): void {
  const centre = positionMilli(context.path, target.progress);
  const radiusSq = stats.splashRadiusMilli * stats.splashRadiusMilli;

  for (const other of context.units) {
    if (other.id === target.id) continue;
    if (!isTargetable(other)) continue;

    const at = positionMilli(context.path, other.progress);
    if (distSq(centre.x, centre.y, at.x, at.y) > radiusSq) continue;

    strike(context, tower, stats, other, raw);
  }
}

function strike(
  context: DamageContext,
  tower: TowerState,
  stats: TowerStats,
  unit: Combatant,
  raw: number
): void {
  if (unit.status === "dead" || unit.status === "reached") return;

  const dealt = damageAfterArmor(raw, unitStats(unit.kind).armor, stats.piercesArmor);
  unit.hp -= dealt;

  context.events.push({
    tick: context.tick,
    type: "hit",
    a: unit.id,
    b: tower.id,
    c: dealt,
  });

  if (stats.slow) applySlow(context, tower, stats, unit);

  if (unit.hp <= 0) {
    unit.hp = 0;
    unit.status = "dead";
    context.events.push({ tick: context.tick, type: "death", a: unit.id, b: tower.id });
  }
}

/**
 * Frost slows a unit to a share of its speed for a while.
 *
 * Slows do NOT stack (OD-11 default): a second Frost Tower refreshes the timer
 * rather than compounding the effect, so two of them cannot chain a Rider into
 * standing still. Movement reads slowPermille while slowUntilTick is in the
 * future — see stepFor() in simulate.ts.
 */
function applySlow(
  context: DamageContext,
  tower: TowerState,
  stats: TowerStats,
  unit: Combatant
): void {
  if (!stats.slow) return;
  unit.slowPermille = stats.slow.speedPermille;
  unit.slowUntilTick = context.tick + stats.slow.durationTicks;
  context.events.push({ tick: context.tick, type: "slow", a: unit.id, b: tower.id });
}
